import { Plus } from "lucide-react";
import type { FC } from "react";

interface FileUploadProps {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
}

const FileUpload: FC<FileUploadProps> = ({ onFilesSelected, disabled = false }) => {
  return (
    <label className={`${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
      <div className={`w-6 h-6 rounded-full bg-white flex items-center justify-center shadow ${
        disabled ? 'bg-gray-100' : 'hover:bg-gray-50'
      }`}>
        <Plus size={16} />
      </div>
      <input
        type="file"
        className="hidden"
        multiple
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (disabled) e.preventDefault();
        }}
        onChange={(e) => {
          if (disabled) return;
          const files = e.target.files;
          if (!files) return;
          onFilesSelected(Array.from(files));
        }}
      />
    </label>
  );
};

export default FileUpload;

export function getFileIcon(file: File) {
  const name = file.name.toLowerCase();
  if (name.match(/\.(png|jpe?g|gif|bmp|webp|svg)$/)) return <img src="/icons/image.png" alt="image" className="w-4 h-4 inline-block mr-1" />;
  if (name.endsWith('.pdf')) return <img src="/icons/pdf.png" alt="pdf" className="w-4 h-4 inline-block mr-1" />;
  if (name.match(/\.(csv|xls|xlsx)$/)) return <img src="/icons/table.png" alt="table" className="w-4 h-4 inline-block mr-1" />;
  return <img src="/icons/paperclip.png" alt="attachment" className="w-4 h-4 inline-block mr-1" />;
}


// Helper component for attached files display
export function AttachedFilesList({ 
  attachedFiles, 
  onRemoveFile 
}: { 
  attachedFiles: File[], 
  onRemoveFile: (name: string) => void 
}) {
  if (attachedFiles.length === 0) return null;

  return (
    <div className="flex gap-1 flex-wrap mb-1">
      {attachedFiles.map((file, idx) => (
        <span key={idx} className="flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs mr-1">
          {getFileIcon(file)} {file.name}
          <button
            type="button"
            className="ml-1 text-blue-500 hover:text-red-500 focus:outline-none"
            onClick={() => onRemoveFile(file.name)}
            tabIndex={-1}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
}

// Helper component for upload status
export function UploadStatus({ isUploading }: { isUploading: boolean }) {
  if (!isUploading) return null;

  return (
    <div className="flex items-center gap-2 text-xs text-blue-700 mt-2">
      <svg className="animate-spin h-4 w-4 mr-1 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
      </svg>
      Uploading files...
    </div>
  );
}